import PropTypes from "prop-types";
import { useState, useRef, useEffect, memo } from "react";

// Layouts
import AccessCardsContent from "@/Layouts/components/AccessCardsContent";
import AccessDialog from "@/Layouts/components/Dialogs/AccessDialog";
import AccessLists from "@/Layouts/components/AccessLists";
import DoubleCheckingButton from "@/Layouts/components/Buttons/DoubleCheckingButton";
import TitleEdit from "@/Layouts/components/TitleEdit";

// MUI
import { Divider, Grid, Skeleton } from "@mui/material";

// Icons
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { AccessListUsers } from "@/Layouts/components/AccessLists/AccessListUsers";

const isExecutingOperation = (action, index, actionOnIndex) => action && index === actionOnIndex;

function GroupAccessViewComponent(props) {
	const {
		index = 0,
		title = "New Group",
		description = "New group description",
		data = {
			actions: [],
			users: [],
		},
		locationId = "",
		isEditable = false,
		loading = false,
		divider = false,
		adding = null,
		deleting = null,
		addAmount = 1,
		onAddActions = () => {},
		onDeleteActions = () => {},
		onAddUsers = () => {},
		onDeleteUsers = () => {},
		onUpdateGroup = () => {},
		onDeleteGroup = () => {},
	} = props;

	const [deleteAction, setDeleteAction] = useState(null);
	const [deleteUser, setDeleteUser] = useState(null);
	const [actionOnIndex, setActionOnIndex] = useState("");

	const [localTitle, setLocalTitle] = useState(title);
	const [localDescription, setLocalDescription] = useState(description);

	const addedActions = useRef([]);
	const addedUsers = useRef([]);

	const [openActions, setOpenActions] = useState(false);
	const [openUsers, setOpenUsers] = useState(false);

	const handleCloseActions = () => {
		setOpenActions(false);
		addedActions.current = [];
	};

	const handleCloseUsers = () => {
		setOpenUsers(false);
		addedUsers.current = [];
	};

	const handleAddActions = () => {
		onAddActions?.(addedActions.current, index, locationId);
		addedActions.current = [];
		setActionOnIndex(index);
		setOpenActions(false);
	};

	const handleDeleteAction = (action) => {
		setDeleteAction(action);
		setActionOnIndex(index);
		onDeleteActions?.([action], index, locationId);
	};

	const handleAddUsers = () => {
		onAddUsers?.(addedUsers.current, index, locationId);
		addedUsers.current = [];
		setActionOnIndex(index);
		setOpenUsers(false);
	};

	const handleDeleteUser = (user) => {
		setDeleteUser(user);
		setActionOnIndex(index);
		onDeleteUsers?.([user], index, locationId);
	};

	const handleDeleteGroup = () => {
		onDeleteGroup?.(index);
	};

	const handleChangeTitle = (value) => {
		setLocalTitle(value);
		onUpdateGroup?.(value, localDescription, index, locationId);
	};

	const handleChangeDescription = (value) => {
		setLocalDescription(value);
		onUpdateGroup?.(localTitle, value, index, locationId);
	};

	useEffect(() => {
		setLocalTitle(title);
		setLocalDescription(description);

		return () => {
			setOpenActions(false);
			setOpenUsers(false);
			setActionOnIndex("");
			setLocalTitle("New Group");
			setLocalDescription("New group description");
			addedActions.current = [];
			addedUsers.current = [];
		};
	}, [title, description, data]);

	return (
		<Grid item className="scrollable-content" id={index} xs={12} style={{ scrollMarginTop: "155px" }}>
			<AccessDialog
				title={"Actions"}
				open={openActions}
				onCloseAction={handleCloseActions}
				onAcceptAction={handleAddActions}
				buttonText="Add Action(s)"
			>
				<AccessLists
					searchable
					accessType={"actions"}
					access={data.actions}
					locationId={locationId}
					titleField={"actionName"}
					descriptionField={"actionDescription"}
					onChangeSelection={(selection) => {
						addedActions.current = selection;
					}}
				/>
			</AccessDialog>

			<AccessDialog
				title={"Users"}
				open={openUsers}
				onCloseAction={handleCloseUsers}
				onAcceptAction={handleAddUsers}
				buttonText="Add User(s)"
			>
				<AccessListUsers
					searchable
					accessType={"users"}
					access={data.users}
					locationId={locationId}
					titleField={"fullName"}
					descriptionField="locationName"
					preloadedData={data.users}
					onChangeSelection={(selection) => {
						addedUsers.current = selection;
					}}
				/>
			</AccessDialog>

			<Grid container alignItems={"center"}>
				<Grid item mr={1}>
					{loading ? (
						<Skeleton variant="circular" width={35} height={35} sx={{ mr: 1 }} />
					) : (
						<PeopleAltIcon sx={{ fontSize: "40px", color: "var(--neutral-gray)" }} />
					)}
				</Grid>
				<Grid item alignItems="center" xs={true}>
					<TitleEdit noEditable={!isEditable} title={localTitle} loading={loading} onEditComplete={handleChangeTitle} />
				</Grid>
				<Grid item ml={1}>
					{loading ? (
						<Skeleton variant="circular" width={35} height={35} sx={{ marginLeft: "10px" }} />
					) : (
						<DoubleCheckingButton
							title={"Remove Group"}
							endIcon={<DeleteOutlineOutlinedIcon sx={{ marginLeft: "-4px" }} />}
							mainButtonClasses={"btn-simple-remove btn-pack-remove"}
							disabled={!isEditable}
							confirmationSx={{ right: 0, left: "-90px", width: "130px !important" }}
							onDeleteAction={handleDeleteGroup}
						/>
					)}
				</Grid>
			</Grid>

			<Grid container alignItems={"center"}>
				<Grid item alignItems="center" ml={!loading ? "-7px" : 0} mt={loading && "3px"} xs={true}>
					<TitleEdit
						noEditable={!isEditable}
						title={localDescription}
						classes={"section-description"}
						loading={loading}
						onEditComplete={handleChangeDescription}
					/>
				</Grid>
			</Grid>

			<Grid container mt={2}>
				<AccessCardsContent
					index={index}
					locationId={locationId}
					data={data.actions || []}
					rootIdentifier={"category"}
					cardTitle={"actionName"}
					cardContent={"actionDescription"}
					title={"Actions"}
					cardProperties={{
						subheader: true,
					}}
					buttonTitle={"Add New Action"}
					addAmount={addAmount}
					loading={loading}
					isEditable={isEditable}
					deleteDefinition={{
						identifier: "actionKey",
						action: deleteAction,
					}}
					isAdding={isExecutingOperation(adding?.actions, index, actionOnIndex)}
					isDeleting={isExecutingOperation(deleting?.action, index, actionOnIndex)}
					isContentIncluded={true}
					onOpenAction={() => setOpenActions(true)}
					onDeleteAction={handleDeleteAction}
				/>

				<AccessCardsContent
					index={index}
					locationId={locationId}
					data={data.users || []}
					preloadedData={data.users}
					rootIdentifier={"position"}
					cardTitle={"fullName"}
					cardContent={"locationName"}
					title={"Users"}
					cardProperties={{
						subheader: true,
					}}
					titleColor={"var(--medium-pink-color)"}
					buttonTitle={"Add New User"}
					containerProps={{
						mt: 4,
					}}
					isContentIncluded={true}
					addAmount={addAmount}
					loading={loading}
					isEditable={isEditable}
					deleteDefinition={{
						identifier: "_id",
						action: deleteUser,
					}}
					isAdding={isExecutingOperation(adding?.users, index, actionOnIndex)}
					isDeleting={isExecutingOperation(deleting?.user, index, actionOnIndex)}
					onOpenAction={() => setOpenUsers(true)}
					onDeleteAction={handleDeleteUser}
				/>
			</Grid>

			{divider && (
				<Grid container justifyContent={"center"}>
					<Grid item xs={7}>
						<Divider className="!border-disabled" sx={{ margin: "50px 0px 40px" }} />
					</Grid>
				</Grid>
			)}
		</Grid>
	);
}
GroupAccessViewComponent.propTypes = {
	index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	title: PropTypes.string,
	description: PropTypes.string,
	data: PropTypes.shape({
		_id: PropTypes.string,
		actions: PropTypes.array,
		users: PropTypes.array,
		groupDescription: PropTypes.string,
		groupName: PropTypes.string,
		locationId: PropTypes.string,
	}),
	locationId: PropTypes.string,
	adding: PropTypes.object,
	deleting: PropTypes.object,
	isEditable: PropTypes.bool,
	loading: PropTypes.bool,
	divider: PropTypes.bool,
	addAmount: PropTypes.number,
	onAddActions: PropTypes.func,
	onDeleteActions: PropTypes.func,
	onAddUsers: PropTypes.func,
	onDeleteUsers: PropTypes.func,
	onUpdateGroup: PropTypes.func,
	onDeleteGroup: PropTypes.func,
};

const GroupAccessView = memo(GroupAccessViewComponent);
export default GroupAccessView;
